import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Modal, Popconfirm, Select, Space, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import type { CalendarFestival } from '@/types/matchPushes';

interface FestivalFormValues {
  name: string;
  startsOn: Dayjs;
  endsOn?: Dayjs | null;
  countries: string[];
  regions: string[];
  notes?: string;
}

const ISO2 = /^[A-Z]{2}$/;
const DAY = 'YYYY-MM-DD';

interface Props {
  open: boolean;
  /** The festival to edit; null adds one. */
  festival: CalendarFestival | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Add or edit a festival (a day pushes and live events can be planned around). */
export const FestivalFormModal: React.FC<Props> = ({ open, festival, onClose, onSaved }) => {
  const [form] = Form.useForm<FestivalFormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (festival) {
      form.setFieldsValue({
        name: festival.name,
        startsOn: dayjs(festival.startsOn),
        endsOn: festival.endsOn ? dayjs(festival.endsOn) : null,
        countries: festival.countries,
        regions: festival.regions,
        notes: festival.notes ?? undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ countries: ['IN'], regions: [] });
    }
  }, [open, festival, form]);

  const submit = async () => {
    let values: FestivalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    const body = {
      name: values.name.trim(),
      startsOn: values.startsOn.format(DAY),
      endsOn: values.endsOn ? values.endsOn.format(DAY) : null,
      countries: values.countries,
      regions: values.regions,
      notes: values.notes?.trim() || null,
    };
    setSaving(true);
    try {
      if (festival) {
        await adminApi.cricketCalendar.updateFestival(festival.id, body);
      } else {
        await adminApi.cricketCalendar.createFestival(body);
      }
      message.success(festival ? 'Festival saved' : 'Festival added');
      onSaved();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save the festival'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!festival) return;
    setSaving(true);
    try {
      await adminApi.cricketCalendar.deleteFestival(festival.id);
      message.success('Festival deleted');
      onSaved();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to delete the festival'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={festival ? 'Edit festival' : 'Add festival'}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={festival ? 'Save' : 'Add festival'}
      confirmLoading={saving}
      destroyOnClose
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space>
          {festival && (
            <Popconfirm
              title="Delete this festival?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={remove}
            >
              <a style={{ color: '#ff4d4f' }}>Delete</a>
            </Popconfirm>
          )}
          <CancelBtn />
          <OkBtn />
        </Space>
      )}
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, max: 100 }]}
        >
          <Input placeholder="Diwali" />
        </Form.Item>
        <Space size={12} style={{ display: 'flex' }} align="start">
          <Form.Item name="startsOn" label="Starts on" rules={[{ required: true }]}>
            <DatePicker format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="endsOn" label="Ends on (optional)">
            <DatePicker format="DD MMM YYYY" />
          </Form.Item>
        </Space>
        <Form.Item
          name="countries"
          label="Countries"
          extra="ISO-2 codes such as IN, PK, BD."
          normalize={(value: string[] | undefined) =>
            (value ?? []).map((c) => c.trim().toUpperCase())
          }
          rules={[
            {
              validator: (_, value: string[] | undefined) =>
                (value ?? []).every((c) => ISO2.test(c))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Use two-letter country codes')),
            },
          ]}
        >
          <Select mode="tags" tokenSeparators={[',', ' ']} open={false} />
        </Form.Item>
        <Form.Item
          name="regions"
          label="Regions"
          extra="State names as GA4 reports them, e.g. Tamil Nadu. Empty means the whole country."
        >
          <Select mode="tags" tokenSeparators={[',']} open={false} />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
